"use client";

import React, { useState } from "react";
import { 
  X, 
  Building, 
  User, 
  Mail, 
  Phone, 
  Calendar, 
  Car, 
  DollarSign, 
  CheckCircle2, 
  Trash2,
  Save,
  Tag
} from "lucide-react";
import { RealEstateLead } from "@/lib/demos/realEstateDb";

interface RealEstateLeadDetailModalProps {
  lead: RealEstateLead;
  onClose: () => void;
  onLeadUpdated: () => void;
  currency?: "INR" | "USD";
}

const STAGES: RealEstateLead["stage"][] = [
  "New Inquiries",
  "Site Visit Scheduled",
  "Site Visit Completed",
  "Booking Token",
  "Sale Deed (Won)",
  "Lost",
];

export default function RealEstateLeadDetailModal({
  lead,
  onClose,
  onLeadUpdated,
  currency = "INR",
}: RealEstateLeadDetailModalProps) {
  const [stage, setStage] = useState<RealEstateLead["stage"]>(lead.stage);
  const [assignedAgent, setAssignedAgent] = useState(lead.assigned_agent || "Aura Executive Team");
  const [siteVisitDate, setSiteVisitDate] = useState(lead.site_visit_date || "");
  const [siteVisitPickup, setSiteVisitPickup] = useState(lead.site_visit_pickup === 1);
  const [buyerType, setBuyerType] = useState(lead.buyer_type);
  const [notes, setNotes] = useState(lead.notes || "");
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleSave = async () => {
    try {
      setIsSaving(true);
      const res = await fetch(`/api/demos/real-estate/leads/${lead.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          stage,
          assigned_agent: assignedAgent,
          site_visit_date: siteVisitDate || null,
          site_visit_pickup: siteVisitPickup ? 1 : 0,
          buyer_type: buyerType,
          notes,
        }),
      });

      if (res.ok) {
        onLeadUpdated();
        onClose();
      }
    } catch (err) {
      console.error("Failed to update real estate lead:", err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm(`Are you sure you want to remove lead #${lead.id} (${lead.name})?`)) return;
    try {
      setIsDeleting(true);
      const res = await fetch(`/api/demos/real-estate/leads/${lead.id}`, {
        method: "DELETE",
      });
      if (res.ok) {
        onLeadUpdated();
        onClose();
      }
    } catch (err) {
      console.error("Failed to delete lead:", err);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md animate-fadeIn">
      <div className="relative w-full max-w-2xl bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl overflow-hidden text-slate-100 max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-800 bg-slate-950 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-400 font-bold">
              #{lead.id}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-bold text-white font-heading">{lead.name}</h2>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-slate-800 text-amber-400 border border-slate-700">
                  {lead.buyer_type}
                </span>
              </div>
              <p className="text-xs text-slate-400">
                Interested in: <strong className="text-slate-200">{lead.property_name}</strong>
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content Body */}
        <div className="p-6 overflow-y-auto custom-scrollbar space-y-5 flex-grow">
          {/* Quick Info Bar */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-3.5 rounded-xl bg-slate-950 border border-slate-800 text-xs">
            <div>
              <span className="text-[10px] text-slate-500 uppercase font-bold block">Budget Value</span>
              <span className="font-extrabold text-amber-400 text-sm">
                {lead.currency === "INR" ? lead.budget_range : lead.budget_range}
              </span>
            </div>
            <div>
              <span className="text-[10px] text-slate-500 uppercase font-bold block">Lead Channel</span>
              <span className="font-semibold text-slate-300">{lead.lead_source}</span>
            </div>
            <div>
              <span className="text-[10px] text-slate-500 uppercase font-bold block">Asset Type</span>
              <span className="font-semibold text-slate-300">{lead.property_type}</span>
            </div>
            <div>
              <span className="text-[10px] text-slate-500 uppercase font-bold block">Registered Date</span>
              <span className="font-semibold text-slate-300">
                {new Date(lead.created_at).toLocaleDateString()}
              </span>
            </div>
          </div>

          {/* Contact Details */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex items-center gap-2 p-3 rounded-xl bg-slate-800/60 border border-slate-700/60 text-xs">
              <Mail className="w-4 h-4 text-amber-400 shrink-0" />
              <div className="truncate">
                <span className="text-[10px] text-slate-400 block">Email Address</span>
                <a href={`mailto:${lead.email}`} className="font-medium text-white hover:underline truncate block">
                  {lead.email}
                </a>
              </div>
            </div>

            <div className="flex items-center gap-2 p-3 rounded-xl bg-slate-800/60 border border-slate-700/60 text-xs">
              <Phone className="w-4 h-4 text-emerald-400 shrink-0" />
              <div>
                <span className="text-[10px] text-slate-400 block">WhatsApp Phone</span>
                <span className="font-medium text-white">{lead.phone || "Not provided"}</span>
              </div>
            </div>
          </div>

          {/* Pipeline Stage & Agent */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-slate-300 mb-1 uppercase tracking-wider">
                Sales Pipeline Stage *
              </label>
              <select
                value={stage}
                onChange={(e) => setStage(e.target.value as any)}
                className="w-full px-3 py-2 rounded-xl bg-slate-800 border border-slate-700 text-amber-300 font-bold text-xs focus:border-amber-500 focus:outline-none"
              >
                {STAGES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-300 mb-1 uppercase tracking-wider">
                Assigned Private Advisor
              </label>
              <input
                type="text"
                value={assignedAgent}
                onChange={(e) => setAssignedAgent(e.target.value)}
                className="w-full px-3 py-2 rounded-xl bg-slate-800 border border-slate-700 text-white text-xs focus:border-amber-500 focus:outline-none"
              />
            </div>
          </div>

          {/* Site Visit Schedule & Chauffeur */}
          <div className="p-4 rounded-xl bg-slate-950 border border-slate-800 space-y-3">
            <span className="text-xs font-bold uppercase tracking-wider text-amber-400 flex items-center gap-1.5">
              <Calendar className="w-4 h-4" />
              Private Site Visit Coordinates
            </span>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] font-semibold text-slate-400 mb-1">
                  Scheduled Inspection Date &amp; Time
                </label>
                <input
                  type="text"
                  placeholder="e.g. 2026-09-08 11:30 AM"
                  value={siteVisitDate}
                  onChange={(e) => setSiteVisitDate(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-xs focus:border-amber-500 focus:outline-none"
                />
              </div>

              <div className="flex items-center justify-between p-2.5 rounded-lg bg-slate-800 border border-slate-700">
                <div className="flex items-center gap-2">
                  <Car className="w-4 h-4 text-amber-400" />
                  <div>
                    <div className="text-xs font-bold text-white">VIP Chauffeur Pickup</div>
                    <div className="text-[10px] text-slate-400">Mercedes / BMW dispatch</div>
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={siteVisitPickup}
                  onChange={(e) => setSiteVisitPickup(e.target.checked)}
                  className="w-4 h-4 accent-amber-500 rounded cursor-pointer"
                />
              </div>
            </div>
          </div>

          {/* Notes & Client Feedback */}
          <div>
            <label className="block text-xs font-bold text-slate-300 mb-1 uppercase tracking-wider">
              Estate Advisor Notes &amp; Unit Preferences
            </label>
            <textarea
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full px-3 py-2 rounded-xl bg-slate-800 border border-slate-700 text-white text-xs focus:border-amber-500 focus:outline-none"
              placeholder="Record client questions, floor plan customizations, financing terms..."
            />
          </div>
        </div>

        {/* Footer Actions */}
        <div className="px-6 py-4 border-t border-slate-800 bg-slate-950 flex items-center justify-between">
          <button
            type="button"
            onClick={handleDelete}
            disabled={isDeleting}
            className="flex items-center gap-1.5 text-xs text-rose-400 hover:text-rose-300 hover:bg-rose-500/10 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50"
          >
            <Trash2 className="w-3.5 h-3.5" />
            <span>Remove Lead</span>
          </button>

          <div className="flex items-center gap-2.5">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-semibold transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving}
              className="flex items-center gap-1.5 px-5 py-2 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 text-slate-950 font-bold text-xs shadow-md hover:shadow-amber-500/20 transition-all hover:scale-[1.02] disabled:opacity-50"
            >
              <Save className="w-3.5 h-3.5" />
              <span>{isSaving ? "Saving Changes..." : "Save Opportunity"}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
